"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Edit, Trash2, Eye, EyeOff, ImageIcon as ImageIconLucide, Tag } from "lucide-react"
import { bannerService, type Banner, type BannerFormData } from "../../services/bannerService"
import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import toast from "react-hot-toast"
import LoadingSpinner from "../../components/common/LoadingSpinner"
import { collection, getDocs, query as firestoreQuery, where } from "firebase/firestore"
import { db } from "../../firebase/config"

interface ExtendedBannerFormData extends BannerFormData {
  linkedProductId?: string
}

interface ExtendedBanner extends Banner {
  linkedProductId?: string
}

// Product type for linking
interface ProductForLinking {
  id: string
  name: string
  price: number
  image?: string
  images?: string[]
  category?: string
  categories?: string[]
}

// Helper function to slugify strings (matches ProductCard and Home.tsx)
function slugify(str: string | undefined | null): string {
  if (!str) return ""
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

// Generate product URL that matches the routing format
function getProductUrl(product: ProductForLinking): string {
  if (!product) return `/categories`

  const categorySlug = slugify(product.categories?.[0] || product.category || "furniture")
  const nameSlug = slugify(product.name) || product.id
  return `/${categorySlug}/${nameSlug}`
}

interface BannerFormProps {
  banner?: ExtendedBanner
  onSave: (data: ExtendedBannerFormData) => Promise<void>
  onCancel: () => void
  isLoading: boolean
}

const BannerForm: React.FC<BannerFormProps> = ({ banner, onSave, onCancel, isLoading }) => {
  const [formData, setFormData] = useState<ExtendedBannerFormData>({
    title: banner?.title || "",
    subtitle: banner?.subtitle || "",
    description: banner?.description || "",
    image: banner?.image || "",
    ctaText: banner?.ctaText || "Shop Now",
    ctaLink: banner?.ctaLink || "/categories",
    isActive: banner?.isActive ?? true,
    type: banner?.type || "image-only",
    linkedProductId: banner?.linkedProductId || "",
  })
  const [imagePreview, setImagePreview] = useState<string>(banner?.image || "")
  const [products, setProducts] = useState<ProductForLinking[]>([])
  const [productsLoading, setProductsLoading] = useState(false)

  const bannerTemplates = [
    {
      name: "Mega Sale",
      title: "MEGA SALE",
      subtitle: "Up to 70% OFF",
      description: "Limited time offer on premium furniture. Free delivery on orders above ₹25,000!",
      ctaText: "Shop Now",
    },
    {
      name: "New Collection",
      title: "NEW ARRIVALS",
      subtitle: "Fresh Designs",
      description: "Discover our latest furniture collection with modern styles and premium quality.",
      ctaText: "Explore Now",
    },
    {
      name: "Premium Quality",
      title: "PREMIUM FURNITURE",
      subtitle: "Luxury Living",
      description: "Transform your home with our handcrafted premium furniture collection.",
      ctaText: "Shop Premium",
    },
    {
      name: "Flash Deal",
      title: "FLASH DEAL",
      subtitle: "24 Hours Only",
      description: "Don't miss out! Exclusive discounts on selected furniture items.",
      ctaText: "Grab Deal",
    },
    {
      name: "Free Delivery",
      title: "FREE DELIVERY",
      subtitle: "On Orders ₹25,000+",
      description: "Shop now and get free home delivery on all furniture orders above ₹25,000.",
      ctaText: "Order Now",
    },
    {
      name: "Festive Special",
      title: "FESTIVE SPECIAL",
      subtitle: "Celebrate in Style",
      description: "Special festive discounts on dining sets, sofas, and bedroom furniture.",
      ctaText: "Shop Festive",
    },
  ]

  // Fetch products with image and category data
  useEffect(() => {
    const fetchProducts = async () => {
      setProductsLoading(true)
      try {
        const productsRef = collection(db, "products")
        const productsQuery = firestoreQuery(productsRef, where("isActive", "==", true))
        const productsSnapshot = await getDocs(productsQuery)
        const allProducts: ProductForLinking[] = []
        productsSnapshot.forEach((doc) => {
          const data = doc.data()

          // Handle categories consistently
          let categories: string[] = []
          if (Array.isArray(data.categories)) {
            categories = data.categories.filter(Boolean)
          } else if (data.category) {
            categories = [data.category]
          } else {
            categories = ["furniture"]
          }

          allProducts.push({
            id: doc.id,
            name: data.name || "",
            price: Number(data.price) || 0,
            image: data.image || "",
            images: Array.isArray(data.images) ? data.images : [],
            category: categories[0] || "furniture", // Primary category
            categories: categories,
          })
        })
        // Sort alphabetically
        allProducts.sort((a, b) => a.name.localeCompare(b.name))
        setProducts(allProducts)
      } catch (error) {
        console.error("Error loading products:", error)
        toast.error("Failed to load products for selection")
      } finally {
        setProductsLoading(false)
      }
    }
    fetchProducts()
  }, [])

  // Auto-populate image when product is selected
  useEffect(() => {
    if (formData.linkedProductId) {
      const selectedProduct = products.find((p) => p.id === formData.linkedProductId)
      if (selectedProduct) {
        const productImage = selectedProduct.images?.[0] || selectedProduct.image
        if (productImage && productImage !== formData.image) {
          setFormData((prev) => ({ ...prev, image: productImage }))
          handleImageUrlChange(productImage)
        }
      }
    }
  }, [formData.linkedProductId, products])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.image) {
      toast.error("Please enter a banner image URL")
      return
    }

    if (!validateImageUrl(formData.image)) {
      toast.error("Please enter a valid image URL")
      return
    }

    // For text-with-image type, validate required text fields
    if (formData.type === "text-with-image") {
      if (!formData.title || !formData.subtitle || !formData.description) {
        toast.error("Please fill in all required text fields for text-with-image banner")
        return
      }
    }

    const finalFormData = { ...formData }

    // FIXED: Set correct CTA link for product linking using proper URL format
    if (formData.linkedProductId) {
      const selectedProduct = products.find((p) => p.id === formData.linkedProductId)
      if (selectedProduct) {
        finalFormData.ctaLink = getProductUrl(selectedProduct)
      }
    }

    try {
      await onSave(finalFormData)
    } catch (error) {
      console.error("Error saving banner:", error)
    }
  }

  const validateImageUrl = (url: string): boolean => {
    if (!url) return false
    try {
      new URL(url)
      return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url)
    } catch {
      return false
    }
  }

  const handleImageUrlChange = (url: string) => {
    setFormData({ ...formData, image: url })
    if (validateImageUrl(url)) {
      setImagePreview(url)
    } else {
      setImagePreview("")
    }
  }

  const selectedProduct = products.find((p) => p.id === formData.linkedProductId)

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6 bg-white rounded-lg shadow">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{banner ? "Edit Banner" : "Add New Banner"}</h3>
      </div>

      {formData.type === "text-with-image" && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-3 flex items-center">
            <span className="mr-2">✨</span>
            Quick Templates - Click to Apply
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {bannerTemplates.map((template, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    title: template.title,
                    subtitle: template.subtitle,
                    description: template.description,
                    ctaText: template.ctaText,
                  }))
                }}
                className="text-left p-3 bg-white border border-blue-200 rounded-lg hover:border-blue-400 hover:shadow-md transition-all duration-200 group"
              >
                <div className="font-medium text-blue-900 text-sm group-hover:text-blue-700">{template.name}</div>
                <div className="text-xs text-blue-600 mt-1 line-clamp-2">
                  {template.title} - {template.subtitle}
                </div>
              </button>
            ))}
          </div>
          <p className="text-xs text-blue-600 mt-2">
            💡 Select a template above to auto-fill the banner content, then customize as needed.
          </p>
        </div>
      )}

      {/* Banner Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Banner Type</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="bannerType"
              value="image-only"
              checked={formData.type === "image-only"}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as "image-only" | "text-with-image" })}
              className="mr-3"
            />
            <div>
              <div className="font-medium text-gray-900">Image Only</div>
              <div className="text-sm text-gray-500">Full-width clickable image banner</div>
            </div>
          </label>
          <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="bannerType"
              value="text-with-image"
              checked={formData.type === "text-with-image"}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as "image-only" | "text-with-image" })}
              className="mr-3"
            />
            <div>
              <div className="font-medium text-gray-900">Sales Banner</div>
              <div className="text-sm text-gray-500">Attractive promotional banner with modern styling</div>
            </div>
          </label>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Tag className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Product Linking</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link to Product (Optional)</label>
                {productsLoading ? (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <LoadingSpinner size="sm" />
                    <span>Loading products...</span>
                  </div>
                ) : (
                  <select
                    value={formData.linkedProductId}
                    onChange={(e) => setFormData({ ...formData, linkedProductId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select a product (or use custom link below)</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} - ₹{product.price.toLocaleString()} ({product.category})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {formData.linkedProductId && selectedProduct && (
                <div className="bg-green-50 border border-green-200 rounded p-2">
                  <p className="text-sm text-green-800">
                    <strong>Selected:</strong> {selectedProduct.name} - ₹{selectedProduct.price.toLocaleString()}
                  </p>
                  <p className="text-xs text-green-600 mt-1">Banner will link to: {getProductUrl(selectedProduct)}</p>
                  <p className="text-xs text-green-600">Product image will be used automatically for the banner</p>
                </div>
              )}

              {!formData.linkedProductId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Or Custom Link</label>
                  <input
                    type="text"
                    value={formData.ctaLink}
                    onChange={(e) => setFormData({ ...formData, ctaLink: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="/categories"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Left Column - Text Fields (only for text-with-image type) */}
        <div className="space-y-4">
          {formData.type === "text-with-image" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="MEGA SALE"
                  required={formData.type === "text-with-image"}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle *</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Up to 50% OFF"
                  required={formData.type === "text-with-image"}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  rows={3}
                  placeholder="Limited time offer on premium furniture. Free delivery on orders above ₹25,000!"
                  required={formData.type === "text-with-image"}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CTA Button Text</label>
                <input
                  type="text"
                  value={formData.ctaText}
                  onChange={(e) => setFormData({ ...formData, ctaText: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Shop Now"
                />
              </div>
            </>
          )}

          {formData.type === "image-only" && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <ImageIconLucide className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-blue-900">Clickable Image Banner</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    This banner will display as a full-width clickable image. Users can click anywhere on the image to
                    navigate to the selected product or custom link. Recommended size: 1920x600px or higher.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
              Active (show on homepage)
            </label>
          </div>
        </div>

        {/* Right Column - Image URL Input */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Banner Image URL *
              {formData.type === "image-only" && (
                <span className="text-blue-600 text-xs ml-1">(Full-width clickable)</span>
              )}
            </label>

            {/* Image URL Input */}
            <div className="space-y-4">
              <input
                type="url"
                value={formData.image}
                onChange={(e) => handleImageUrlChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="https://example.com/banner-image.jpg"
                required
                disabled={!!formData.linkedProductId}
              />

              {formData.linkedProductId && (
                <p className="text-sm text-blue-600">Image automatically populated from selected product</p>
              )}

              {formData.image && !validateImageUrl(formData.image) && (
                <p className="text-sm text-red-600">
                  Please provide a valid image URL (jpg, jpeg, png, gif, webp, svg)
                </p>
              )}

              <p className="text-xs text-gray-500">
                {formData.type === "image-only"
                  ? "Recommended: 1920x600px or higher for full-width display"
                  : "Recommended: 1200x600px for optimal display"}
              </p>
            </div>

            {/* Image Preview */}
            {imagePreview && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Preview:</label>
                <div className="relative">
                  <img
                    src={imagePreview || "/placeholder.svg"}
                    alt="Banner preview"
                    className={`w-full rounded-lg border border-gray-200 ${
                      formData.type === "image-only" ? "h-32 object-cover cursor-pointer" : "h-48 object-cover"
                    }`}
                    onError={(e) => {
                      e.currentTarget.src = "/placeholder.svg?height=200&width=400&text=Invalid+Image+URL"
                    }}
                  />
                  <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                    {formData.type === "image-only" ? "Clickable full-width" : "Sales banner style"}
                  </div>
                </div>
              </div>
            )}
          </div>

          {formData.type === "text-with-image" && formData.title && formData.subtitle && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Enhanced Mobile Preview:</h4>

              {/* Mobile Preview */}
              <div className="block md:hidden">
                <div className="relative min-h-[280px] bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-2xl overflow-hidden shadow-2xl">
                  {/* Animated Background Elements */}
                  <div className="absolute inset-0">
                    <div className="absolute top-8 left-6 w-16 h-16 bg-white/10 rounded-full blur-xl animate-pulse"></div>
                    <div className="absolute bottom-12 right-4 w-12 h-12 bg-yellow-300/20 rounded-full blur-lg animate-bounce"></div>
                    <div className="absolute top-1/2 left-3 w-8 h-8 bg-pink-300/15 rounded-full blur-md animate-pulse delay-700"></div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10 h-full flex flex-col justify-center items-center text-center p-6">
                    {/* Badge */}
                    <div className="inline-flex items-center px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-medium mb-3 border border-white/30">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                      Limited Time Offer
                    </div>

                    {/* Title */}
                    <h1 className="text-2xl font-black text-white leading-tight mb-2 tracking-tight">
                      {formData.title}
                    </h1>

                    {/* Subtitle */}
                    <div className="text-lg font-bold text-yellow-300 mb-3 drop-shadow-lg">{formData.subtitle}</div>

                    {/* Description */}
                    {formData.description && (
                      <p className="text-sm text-white/90 leading-relaxed max-w-xs mx-auto mb-4 font-medium">
                        {formData.description}
                      </p>
                    )}

                    {/* CTA Buttons */}
                    <div className="flex flex-col w-full max-w-xs space-y-2">
                      <span className="bg-white text-gray-900 px-6 py-3 rounded-2xl font-bold text-sm shadow-xl">
                        {formData.ctaText || "Shop Now"} →
                      </span>
                      <span className="bg-white/10 backdrop-blur-sm border border-white/40 text-white px-6 py-3 rounded-2xl font-semibold text-sm">
                        Browse All Categories
                      </span>
                    </div>
                  </div>

                  {/* Rating Badge */}
                  <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-2xl shadow-xl">
                    <div className="flex items-center space-x-1">
                      <span className="text-yellow-400 text-sm">★</span>
                      <div className="text-right">
                        <div className="font-black text-gray-900 text-sm leading-none">4.6</div>
                        <div className="text-xs text-gray-600">200+</div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-pink-400 to-blue-400"></div>
                </div>

                {/* Preview Label */}
                <div className="mt-2 text-center">
                  <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                    ✨ Enhanced Mobile Preview
                  </span>
                </div>
              </div>

              {/* Desktop Preview - Enhanced */}
              <div className="hidden md:block">
                <div className="relative w-full h-[400px] lg:h-[500px] xl:h-[600px] bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg overflow-hidden shadow-xl">
                  <div className="px-4 lg:px-8 h-full">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center h-full py-8 lg:py-16">
                      <div className="space-y-4 lg:space-y-6 text-center lg:text-left z-20 relative order-2 lg:order-1">
                        <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-primary-900 leading-tight">
                          {formData.title}
                          <span className="text-primary-600 block text-lg md:text-xl lg:text-2xl xl:text-3xl">
                            {formData.subtitle}
                          </span>
                        </h1>
                        <p className="text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                          {formData.description}
                        </p>
                        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 justify-center lg:justify-start pt-2">
                          <span className="bg-primary-600 text-white px-6 lg:px-8 py-3 lg:py-4 rounded-lg font-medium text-center text-sm md:text-base shadow-lg">
                            {formData.ctaText || "Shop Now"}
                          </span>
                          <span className="border-2 border-primary-600 text-primary-600 px-6 lg:px-8 py-3 lg:py-4 rounded-lg font-medium text-center text-sm md:text-base">
                            Browse Categories
                          </span>
                        </div>
                      </div>
                      {imagePreview && (
                        <div className="relative order-1 lg:order-2">
                          <img
                            src={imagePreview || "/placeholder.svg"}
                            alt={`${formData.title} - ${formData.subtitle}`}
                            className="rounded-xl lg:rounded-2xl shadow-xl lg:shadow-2xl w-full h-[200px] md:h-[250px] lg:h-[300px] xl:h-[400px] object-cover object-center"
                          />
                          <div className="absolute -bottom-2 -left-2 lg:-bottom-4 lg:-left-4 bg-white p-2 lg:p-3 rounded-lg lg:rounded-xl shadow-lg">
                            <div className="flex items-center space-x-1 lg:space-x-2">
                              <span className="text-yellow-400 text-xs lg:text-sm">★</span>
                              <span className="font-semibold text-gray-900 text-xs lg:text-sm">4.6/5</span>
                            </div>
                            <p className="text-xs text-gray-600">200+ Reviews</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                    DESKTOP PREVIEW
                  </div>
                </div>
              </div>

              {/* Enhanced Note */}
              <div className="mt-3 p-4 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <span className="text-green-600 text-lg">🎨</span>
                  <div>
                    <p className="text-sm font-medium text-green-800 mb-1">Enhanced Mobile Experience</p>
                    <p className="text-xs text-green-700">
                      Mobile banners now feature a stunning gradient background with animated elements, modern
                      typography, and improved call-to-action buttons for maximum engagement and conversion.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Preview for image-only type */}
          {formData.type === "image-only" && imagePreview && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Clickable Banner Preview:</h4>
              <div className="bg-gray-100 rounded-lg overflow-hidden relative cursor-pointer hover:shadow-lg transition-shadow">
                <img
                  src={imagePreview || "/placeholder.svg"}
                  alt="Clickable banner preview"
                  className="w-full h-24 object-cover"
                />
                <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center">
                  <div className="bg-white/90 px-3 py-1 rounded-full text-xs font-medium opacity-0 hover:opacity-100 transition-opacity">
                    Click to navigate
                  </div>
                </div>
                {/* Preview label */}
                <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs">PREVIEW</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-center"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          {isLoading && <LoadingSpinner size="sm" />}
          <span>{banner ? "Update Banner" : "Create Banner"}</span>
        </button>
      </div>
    </form>
  )
}

// FIXED: Updated SortableBannerItem to show correct product URL
const SortableBannerItem: React.FC<{
  banner: ExtendedBanner
  products: ProductForLinking[]
  onEdit: (banner: ExtendedBanner) => void
  onDelete: (id: string) => void
  onToggleActive: (id: string, isActive: boolean) => void
}> = ({ banner, products, onEdit, onDelete, onToggleActive }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: banner.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  // Find linked product and generate correct URL
  const linkedProduct = banner.linkedProductId ? products.find((p) => p.id === banner.linkedProductId) : null
  const displayUrl = linkedProduct ? getProductUrl(linkedProduct) : banner.ctaLink

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border rounded-lg p-4 ${
        isDragging ? "opacity-50" : ""
      } ${banner.isActive ? "border-green-200 bg-green-50" : "border-gray-200"}`}
    >
      <div className="flex items-start space-x-4">
        {/* Drag Handle */}
        <div {...attributes} {...listeners} className="cursor-move p-2 text-gray-400 hover:text-gray-600">
          ☰
        </div>

        {/* Banner Image */}
        <div className="flex-shrink-0">
          <img
            src={banner.image || "/placeholder.svg?height=64&width=128"}
            alt={banner.type === "image-only" ? "Clickable image banner" : `${banner.title} ${banner.subtitle}`}
            className={`w-32 h-16 object-cover rounded-lg border border-gray-200 ${
              banner.type === "image-only" ? "cursor-pointer" : ""
            }`}
            onError={(e) => {
              e.currentTarget.src = "/placeholder.svg?height=64&width=128"
            }}
          />
        </div>

        {/* Banner Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    banner.type === "image-only" ? "bg-blue-100 text-blue-800" : "bg-orange-100 text-orange-800"
                  }`}
                >
                  {banner.type === "image-only" ? "Clickable Image" : "Sales Banner"}
                </span>
                {banner.linkedProductId && (
                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 flex items-center space-x-1">
                    <Tag className="w-3 h-3" />
                    <span>Product Linked</span>
                  </span>
                )}
              </div>

              {banner.type === "text-with-image" ? (
                <>
                  <h4 className="text-lg font-semibold text-gray-900">
                    {banner.title} <span className="text-orange-600">{banner.subtitle}</span>
                  </h4>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{banner.description}</p>
                </>
              ) : (
                <>
                  <h4 className="text-lg font-semibold text-gray-900">Clickable Image Banner</h4>
                  <p className="text-sm text-gray-600 mt-1">Full-width clickable image banner</p>
                </>
              )}

              <div className="flex items-center space-x-4 mt-2">
                <span className="text-xs text-gray-500">Order: {banner.order}</span>
                <span className="text-xs text-gray-500">Links to: {displayUrl}</span>
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    banner.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {banner.isActive ? "🔗 Live" : "⏸️ Hidden"}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button
                onClick={() => onToggleActive(banner.id, !banner.isActive)}
                className={`p-2 rounded-lg transition-colors ${
                  banner.isActive ? "text-green-600 hover:bg-green-100" : "text-gray-400 hover:bg-gray-100"
                }`}
                title={banner.isActive ? "Hide banner" : "Show banner"}
              >
                {banner.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>

              <button
                onClick={() => onEdit(banner)}
                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                title="Edit banner"
              >
                <Edit className="w-4 h-4" />
              </button>

              <button
                onClick={() => {
                  if (window.confirm("Are you sure you want to delete this banner?")) {
                    onDelete(banner.id)
                  }
                }}
                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                title="Delete banner"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const AdminBanners: React.FC = () => {
  const [banners, setBanners] = useState<ExtendedBanner[]>([])
  const [products, setProducts] = useState<ProductForLinking[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingBanner, setEditingBanner] = useState<ExtendedBanner | null>(null)

  const loadBanners = async () => {
    try {
      setIsLoading(true)
      const allBanners = await bannerService.getBanners()
      setBanners(allBanners)
    } catch (error) {
      console.error("Error loading banners:", error)
      toast.error("Failed to load banners")
    } finally {
      setIsLoading(false)
    }
  }

  // Load products for URL generation in banner list
  const loadProducts = async () => {
    try {
      const productsRef = collection(db, "products")
      const productsQuery = firestoreQuery(productsRef, where("isActive", "==", true))
      const productsSnapshot = await getDocs(productsQuery)
      const allProducts: ProductForLinking[] = []
      productsSnapshot.forEach((doc) => {
        const data = doc.data()

        // Handle categories consistently
        let categories: string[] = []
        if (Array.isArray(data.categories)) {
          categories = data.categories.filter(Boolean)
        } else if (data.category) {
          categories = [data.category]
        } else {
          categories = ["furniture"]
        }

        allProducts.push({
          id: doc.id,
          name: data.name || "",
          price: Number(data.price) || 0,
          image: data.image || "",
          images: Array.isArray(data.images) ? data.images : [],
          category: categories[0] || "furniture", // Primary category
          categories: categories,
        })
      })
      setProducts(allProducts)
    } catch (error) {
      console.error("Error loading products:", error)
    }
  }

  const triggerHomepageUpdate = () => {
    // Dispatch custom event for homepage to listen
    window.dispatchEvent(new CustomEvent("bannerUpdate"))

    // Also use localStorage as backup
    localStorage.setItem("bannerUpdate", Date.now().toString())
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const activeIndex = banners.findIndex((banner) => banner.id === active.id)
    const overIndex = banners.findIndex((banner) => banner.id === over.id)

    if (activeIndex !== -1 && overIndex !== -1) {
      const newBanners = [...banners]
      const [reorderedBanner] = newBanners.splice(activeIndex, 1)
      newBanners.splice(overIndex, 0, reorderedBanner)

      // Update order values
      const updatedBanners = newBanners.map((banner, index) => ({
        ...banner,
        order: index + 1,
      }))

      setBanners(updatedBanners)

      try {
        // Update order in backend
        await Promise.all(
          updatedBanners.map((banner) => bannerService.updateBanner(banner.id, { order: banner.order } as any)),
        )
        toast.success("Banner order updated successfully!")
        triggerHomepageUpdate()
      } catch (error) {
        console.error("Error updating banner order:", error)
        toast.error("Failed to update banner order")
        // Revert on error
        loadBanners()
      }
    }
  }

  useEffect(() => {
    loadBanners()
    loadProducts()
  }, [])

  const handleCreateBanner = async (data: ExtendedBannerFormData) => {
    try {
      setIsSubmitting(true)

      // Check if we're at the 10 banner limit
      if (banners.length >= 10) {
        toast.error("Maximum 10 banners allowed. Please delete a banner before adding a new one.")
        return
      }

      await bannerService.createBanner(data)
      toast.success("Banner created successfully!")
      setShowAddForm(false)
      await loadBanners()
      triggerHomepageUpdate()
    } catch (error) {
      console.error("Error creating banner:", error)
      toast.error("Failed to create banner")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdateBanner = async (data: ExtendedBannerFormData) => {
    if (!editingBanner) return

    try {
      setIsSubmitting(true)
      await bannerService.updateBanner(editingBanner.id, data)
      toast.success("Banner updated successfully!")
      setEditingBanner(null)
      await loadBanners()
      triggerHomepageUpdate()
    } catch (error) {
      console.error("Error updating banner:", error)
      toast.error("Failed to update banner")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteBanner = async (id: string) => {
    try {
      await bannerService.deleteBanner(id)
      toast.success("Banner deleted successfully!")
      await loadBanners()
      triggerHomepageUpdate()
    } catch (error) {
      console.error("Error deleting banner:", error)
      toast.error("Failed to delete banner")
    }
  }

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      await bannerService.updateBanner(id, { isActive })
      toast.success(`Banner ${isActive ? "activated" : "deactivated"} successfully!`)
      await loadBanners()
      triggerHomepageUpdate()
    } catch (error) {
      console.error("Error toggling banner active state:", error)
      toast.error("Failed to toggle banner active state")
    }
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Banners</h2>
          <p className="text-sm text-gray-600 mt-1">Manage homepage banners ({banners.length}/10 banners)</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          disabled={banners.length >= 10}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
        >
          {banners.length >= 10 ? "Banner Limit Reached" : "Add New Banner"}
        </button>
      </div>

      {(showAddForm || editingBanner) && (
        <div className="mb-8">
          <BannerForm
            banner={editingBanner || undefined}
            onSave={editingBanner ? handleUpdateBanner : handleCreateBanner}
            onCancel={() => {
              setShowAddForm(false)
              setEditingBanner(null)
            }}
            isLoading={isSubmitting}
          />
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : banners.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="max-w-md mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Banners Created</h3>
            <p className="text-gray-600 mb-4">Create your first banner to start showcasing content on the homepage.</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700"
            >
              Create First Banner
            </button>
          </div>
        </div>
      ) : (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={banners.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {banners.map((banner) => (
                <SortableBannerItem
                  key={banner.id}
                  banner={banner}
                  products={products}
                  onEdit={setEditingBanner}
                  onDelete={handleDeleteBanner}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Help Section */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Banner Management Tips</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Drag banners to reorder them on the homepage</li>
          <li>• Link banners to products for automatic image population</li>
          <li>• Use "Sales Banner" type for promotional content with text overlay</li>
          <li>• Use "Image Only" type for full-width clickable images</li>
          <li>• Maximum 10 banners allowed for optimal performance</li>
        </ul>
      </div>
    </div>
  )
}

export default AdminBanners
